/**
 * Admin: Synchronize Search Index
 *
 * Wraps the `syncSearchIndex` endpoint. Default mode is dry run; the apply
 * button only enables after the user has reviewed at least one sample panel.
 * See ADMIN_PORTAL_UX_REQUIREMENTS.md §4.
 *
 * Honors `?skipCleanup=1` and `?skipAdd=1` query string params used by
 * deep-links from the Search Index Health page.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Play, Wand2 } from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import { useAuth } from '../contexts/AuthContext';
import {
  syncSearchIndex,
  saveLastRun,
  loadLastRun,
  type SyncSearchIndexResponse,
  type LastRunRecord,
} from '../services/adminApi';
import {
  Card,
  CardTitle,
  PrimaryButton,
  SecondaryButton,
  RunningIndicator,
  ConfirmDialog,
  Banner,
  StatGrid,
  Metric,
  RawJson,
  LastRunCard,
  Field,
  Label,
  InlineLabel,
  ButtonRow,
  SampleTable,
} from '../components/admin/AdminUI';
import { formatEventLocalDateTime } from '../utils/eventDateTime';

const ACTION = 'syncSearchIndex';

export const AdminSyncIndexPage: React.FC = () => {
  const { userInfo } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [doAdd, setDoAdd] = useState<boolean>(params.get('skipAdd') !== '1');
  const [doCleanup, setDoCleanup] = useState<boolean>(params.get('skipCleanup') !== '1');

  const [dryRun, setDryRun] = useState<SyncSearchIndexResponse | null>(null);
  const [applied, setApplied] = useState<SyncSearchIndexResponse | null>(null);
  const [appliedStatus, setAppliedStatus] = useState<number | null>(null);
  const [reviewedSample, setReviewedSample] = useState(false);
  const [running, setRunning] = useState(false);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lastRun, setLastRun] = useState<LastRunRecord<SyncSearchIndexResponse> | null>(null);

  useEffect(() => { setLastRun(loadLastRun(ACTION)); }, []);

  // Browser-warn the user if they reload mid-apply
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (running) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [running]);

  const formInvalid = !doAdd && !doCleanup;

  const eta = useMemo(() => {
    if (!dryRun) return null;
    const missing = dryRun.addition?.missingCount ?? 0;
    return Math.round(missing * 0.4 + 3); // seconds
  }, [dryRun]);

  const callSync = async (mode: 'dry' | 'apply') => {
    setRunning(true);
    setError(null);
    setStartedAt(Date.now());
    if (mode === 'dry') { setDryRun(null); setApplied(null); }
    try {
      const { data, status } = await syncSearchIndex({
        dryRun: mode === 'dry',
        skipCleanup: !doCleanup,
        skipAdd: !doAdd,
      });
      if (mode === 'dry') {
        setDryRun(data);
        setReviewedSample(false);
      } else {
        setApplied(data);
        setAppliedStatus(status);
        const record: LastRunRecord<SyncSearchIndexResponse> = {
          timestamp: new Date().toISOString(),
          triggeredBy: userInfo.userName || userInfo.userId,
          durationMs: data.summary?.durationMs,
          summary: {
            deleted: data.cleanup?.deletedCount ?? 0,
            added: data.addition?.addedCount ?? 0,
            inSync: !!data.summary?.inSync,
          },
          response: data,
        };
        saveLastRun(ACTION, record);
        setLastRun(record);
      }
    } catch (e: any) {
      setError(e?.message ?? 'Sync failed');
    } finally {
      setRunning(false);
      setStartedAt(null);
    }
  };

  const handleApplyClick = () => {
    if (!reviewedSample || formInvalid || running) return;
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    await callSync('apply');
  };

  return (
    <AdminLayout
      currentPage="syncIndex"
      pageTitle="Synchronize Search Index"
      pageDescription="Reconcile the SQL database with the Azure AI Search index."
    >
      <Banner tone="info">
        This tool reconciles the database with the search index. <strong>Run a
        dry run first</strong> and apply only after reviewing the preview.
      </Banner>

      <Card>
        <CardTitle>Plan the sync</CardTitle>
        <Field>
          <Label>What to do</Label>
          <div>
            <InlineLabel>
              <input
                type="checkbox"
                checked={doAdd}
                onChange={(e) => setDoAdd(e.target.checked)}
              />
              Add missing events to the search index
            </InlineLabel>
            <InlineLabel>
              <input
                type="checkbox"
                checked={doCleanup}
                onChange={(e) => setDoCleanup(e.target.checked)}
              />
              Remove orphaned documents from the search index
            </InlineLabel>
          </div>
          {formInvalid && (
            <div style={{ fontSize: 13, color: '#cf1322', marginTop: 6 }}>
              Choose at least one phase to run.
            </div>
          )}
        </Field>

        <ButtonRow>
          <PrimaryButton onClick={() => callSync('dry')} disabled={formInvalid || running}>
            <Play size={16} />
            Run dry run
          </PrimaryButton>
        </ButtonRow>

        {running && startedAt != null && (
          <RunningIndicator
            startedAt={startedAt}
            message={dryRun ? 'Applying changes…' : 'Running dry run…'}
          />
        )}
        {error && <Banner tone="error">{error}</Banner>}
      </Card>

      {dryRun && !applied && (
        <Card>
          <CardTitle>Dry run preview</CardTitle>
          <StatGrid>
            <Metric label="DB events" value={dryRun.initial.databaseCount.toLocaleString()} />
            <Metric label="Search docs" value={dryRun.initial.searchCount.toLocaleString()} />
            <Metric label="Δ" value={dryRun.initial.difference.toLocaleString()} />
            <Metric
              label="Orphaned to delete"
              value={dryRun.cleanup.orphanedCount.toLocaleString()}
              tone={dryRun.cleanup.orphanedCount > 0 ? 'warning' : 'neutral'}
            />
            <Metric
              label="Missing to add"
              value={dryRun.addition.missingCount.toLocaleString()}
              tone={dryRun.addition.missingCount > 0 ? 'warning' : 'neutral'}
            />
            <Metric
              label="Expected after apply"
              value={dryRun.summary.expectedFinalCount.toLocaleString()}
              tone={dryRun.summary.inSync ? 'success' : 'warning'}
            />
          </StatGrid>

          {eta != null && eta > 5 && (
            <div style={{ fontSize: 13, color: '#595959', marginBottom: 12 }}>
              Estimated apply time: <strong>~{eta}s</strong>.
            </div>
          )}

          <details onToggle={(e) => { if ((e.target as HTMLDetailsElement).open) setReviewedSample(true); }}>
            <summary style={{ cursor: 'pointer', fontWeight: 500, marginBottom: 8 }}>
              ▸ Review samples ({(dryRun.cleanup.sampleOrphans?.length ?? 0) + (dryRun.addition.sampleMissing?.length ?? 0)})
            </summary>
            <div style={{ marginTop: 12 }}>
              {dryRun.cleanup.sampleOrphans && dryRun.cleanup.sampleOrphans.length > 0 && (
                <>
                  <Label>Orphans (sample)</Label>
                  <SampleTable>
                    <thead>
                      <tr>
                        <th>SearchId</th>
                        <th>DB Id</th>
                        <th>Title</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dryRun.cleanup.sampleOrphans.slice(0, 10).map((d, i) => (
                        <tr key={i}>
                          <td>{d.searchId ?? d.id}</td>
                          <td>{d.databaseEventId ?? '—'}</td>
                          <td>{d.title ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </SampleTable>
                </>
              )}
              {dryRun.addition.sampleMissing && dryRun.addition.sampleMissing.length > 0 && (
                <>
                  <Label style={{ marginTop: 16 }}>Missing events (sample)</Label>
                  <SampleTable>
                    <thead>
                      <tr>
                        <th>Id</th>
                        <th>Title</th>
                        <th>Start</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dryRun.addition.sampleMissing.slice(0, 10).map((e: any, i) => (
                        <tr key={i}>
                          <td>{e.id ?? e.Id}</td>
                          <td>{e.title ?? e.Title ?? '—'}</td>
                          <td>{formatEventLocalDateTime(e.startDate ?? e.StartDate) || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </SampleTable>
                </>
              )}
            </div>
          </details>

          <ButtonRow>
            <SecondaryButton onClick={() => setDryRun(null)}>Cancel</SecondaryButton>
            <PrimaryButton
              onClick={handleApplyClick}
              disabled={!reviewedSample || running}
              title={!reviewedSample ? 'Expand the samples panel first' : undefined}
            >
              <Wand2 size={16} />
              Apply changes
            </PrimaryButton>
          </ButtonRow>
          {!reviewedSample && (
            <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 6 }}>
              Expand the samples panel above to enable Apply.
            </div>
          )}

          <RawJson data={dryRun} label="Dry run JSON" />
        </Card>
      )}

      {applied && (
        <Card>
          <CardTitle>Result</CardTitle>
          <Banner tone={applied.summary.inSync && appliedStatus !== 207 ? 'success' : 'warning'}>
            {applied.summary.message}
          </Banner>
          <StatGrid>
            <Metric label="Deleted" value={(applied.cleanup.deletedCount ?? 0).toLocaleString()} tone="success" />
            <Metric label="Added"   value={(applied.addition.addedCount ?? 0).toLocaleString()} tone="success" />
            <Metric label="Final search docs" value={applied.summary.expectedFinalCount.toLocaleString()} />
            {applied.summary.durationMs != null && (
              <Metric label="Duration" value={`${(applied.summary.durationMs / 1000).toFixed(1)}s`} />
            )}
          </StatGrid>

          {(applied.cleanup.errors?.length ?? 0) > 0 && (
            <>
              <Label style={{ marginTop: 16, color: '#cf1322' }}>Cleanup errors</Label>
              <SampleTable>
                <thead><tr><th>#</th><th>Message</th></tr></thead>
                <tbody>
                  {applied.cleanup.errors.map((e, i) => (
                    <tr key={i}><td>{i + 1}</td><td>{e.message}</td></tr>
                  ))}
                </tbody>
              </SampleTable>
            </>
          )}
          {(applied.addition.errors?.length ?? 0) > 0 && (
            <>
              <Label style={{ marginTop: 16, color: '#cf1322' }}>Addition errors</Label>
              <SampleTable>
                <thead><tr><th>#</th><th>Message</th></tr></thead>
                <tbody>
                  {applied.addition.errors.map((e, i) => (
                    <tr key={i}><td>{i + 1}</td><td>{e.message}</td></tr>
                  ))}
                </tbody>
              </SampleTable>
            </>
          )}

          <ButtonRow>
            <SecondaryButton onClick={() => { setApplied(null); setDryRun(null); callSync('dry'); }}>
              Run another dry run
            </SecondaryButton>
            <SecondaryButton onClick={() => navigate('/admin/search-health')}>
              Verify in Health Check
            </SecondaryButton>
          </ButtonRow>

          <RawJson data={applied} />
        </Card>
      )}

      <LastRunCard record={lastRun} />

      <ConfirmDialog
        open={confirmOpen}
        title="Apply sync changes"
        body={
          dryRun ? (
            <>
              <p>You're about to mutate the search index:</p>
              <ul>
                {doCleanup && <li>Delete {dryRun.cleanup.orphanedCount} orphaned documents</li>}
                {doAdd && <li>Add {dryRun.addition.missingCount} missing events</li>}
              </ul>
              <p style={{ marginTop: 8 }}>This is not reversible.</p>
            </>
          ) : null
        }
        confirmPhrase="APPLY"
        primaryLabel="Apply changes"
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      />
    </AdminLayout>
  );
};

export default AdminSyncIndexPage;
